import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, RefreshCw, Mail } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/Api';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../store/authSlice';
import { useSelector } from 'react-redux';

const VerifyEmail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, token, loading } = useSelector((state) => state.auth);

    // Attempt to get email from state (passed from register), fallback to URL query or localStorage if you want
    const initialEmail = user?.email || '';

    const [email, setEmail] = useState(initialEmail);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [timer, setTimer] = useState(60); // 60 seconds cooldown for resend

    useEffect(() => {
        if (!email) {
            // If no email is provided, redirect to register/login or ask user to input email
            // For now, let's just let them stay or redirect if you prefer
        }
    }, [email, navigate]);

    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return;

        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);

        // Focus next input
        if (element.nextSibling && element.value) {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const newOtp = [...otp];
            // If current is empty, focus previous
            // If we really want to delete previous on backspace even if current is empty:
            const prevInput = e.target.previousSibling;
            if (prevInput) {
                prevInput.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = [...otp];
        pastedData.split('').forEach((char, index) => {
            if (index < 6) newOtp[index] = char;
        });
        setOtp(newOtp);
    };

    const dispatch = useDispatch();

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setIsLoading(true);

        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            setError('Le code doit contenir 6 chiffres');
            setIsLoading(false);
            return;
        }

        try {
            const response = await api.post(`/user/verify-email`, {
                email,
                otp: otpCode
            });

            if (response.data.success) {
                setSuccessMessage(response.data.message);

                // Update user in Redux store
                if (response.data.user) {
                    dispatch(updateUser(response.data.user));
                }

                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de la vérification');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (timer > 0) return;

        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await api.post(`/user/resend-verification`, {
                email
            });
            setSuccessMessage('Code renvoyé avec succès');
            setTimer(60);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de l\'envoi du code');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800"
            >
                <div className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Vérifiez votre email</h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Un code à 6 chiffres a été envoyé à :
                        </p>
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="votre@email.com"
                                className="text-center font-semibold text-gray-900 dark:text-gray-100 bg-transparent border-b border-gray-300 dark:border-gray-700 focus:border-yellow-500 outline-none pb-1 w-64"
                            />
                        </div>
                        <p className="text-xs text-gray-400 mb-4">
                            (Modifiez l'email si nécessaire)
                        </p>
                    </div>

                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-xl mb-6 text-sm text-center font-medium"
                        >
                            {error}
                        </motion.div>
                    )}



                    {successMessage ? (
                        <div className="flex flex-col items-center justify-center py-8">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6"
                            >
                                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
                            </motion.div>
                            <motion.h2
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-2xl font-bold text-gray-900 dark:text-white mb-2"
                            >
                                Email Vérifié !
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="text-gray-600 dark:text-gray-400"
                            >
                                Redirection vers le tableau de bord...
                            </motion.p>
                        </div>
                    ) : (
                        <form onSubmit={handleVerify}>
                            <div className="flex justify-center gap-2 mb-8">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(e.target, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onPaste={handlePaste}
                                        className="w-12 h-14 border border-gray-300 dark:border-gray-700 rounded-xl text-center text-2xl font-bold bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 dark:focus:ring-yellow-900/50 outline-none transition-all"
                                    />
                                ))}
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-yellow-500/30 transition-all flex items-center justify-center gap-2"
                            >
                                {isLoading ? 'Vérification...' : 'Confirmer'}
                                {!isLoading && <ArrowRight className="w-5 h-5" />}
                            </motion.button>
                        </form>
                    )}

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                            Vous n'avez pas reçu le code ?
                        </p>
                        <button
                            onClick={handleResend}
                            disabled={timer > 0 || isLoading}
                            className="text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 font-semibold text-sm flex items-center justify-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <RefreshCw className={`w-4 h-4 ${timer > 0 ? '' : 'animate-spin-slow'}`} />
                            {timer > 0 ? `Renvoyer dans ${timer}s` : 'Renvoyer le code'}
                        </button>
                    </div>
                </div>
            </motion.div >
        </div >
    );
};

export default VerifyEmail;
